import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  alpha,
  Stack,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Speed,
  Notifications,
  BarChart,
  Security,
  ArrowForward,
  CheckCircle,
  PlayCircle,
  Star,
  TrendingUp,
  Shield,
  Email,
  Phone,
  LocationOn,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  FlashOn as Zap,
  Timeline as Activity,
  AccessTime as Clock,
  Public as Globe,
  Code,
  Storage as Database,
} from '@mui/icons-material'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import api from '../../api/axiosInstance'
import { useFormik } from 'formik'
import { landingFormSchema } from '../../utils/formSchema/formSchemas'
import screenPng from "../../assets/images/screen.png"

const features = [
  {
    icon: <Activity sx={{ fontSize: 40 }} />,
    title: 'Gerçek Zamanlı İzleme',
    description: 'Web sitenizin performansını milisaniye hassasiyetinde anlık takip edin.',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    icon: <Notifications sx={{ fontSize: 40 }} />,
    title: 'Akıllı Bildirimler',
    description: 'SMS, Email ve Webhook entegrasyonları ile anında haberdar olun.',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  {
    icon: <BarChart sx={{ fontSize: 40 }} />,
    title: 'Detaylı Analizler',
    description: 'Gelişmiş dashboard ve raporlama araçları ile derinlemesine analiz.',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  {
    icon: <Shield sx={{ fontSize: 40 }} />,
    title: 'SSL & Güvenlik',
    description: 'Sertifika izleme ve güvenlik açığı tespiti ile sitenizi koruyun.',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  },
  {
    icon: <Globe sx={{ fontSize: 40 }} />,
    title: 'Çoklu Lokasyon',
    description: 'Dünya genelinde 50+ lokasyondan sitenizi test edin.',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  },
  {
    icon: <Code sx={{ fontSize: 40 }} />,
    title: 'API Entegrasyonu',
    description: 'RESTful API ile kendi sistemlerinizle kolayca entegre edin.',
    gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  },
]

const pricingPlans = [
  {
    title: 'Starter',
    price: 'Ücretsiz',
    period: 'Sonsuza kadar',
    description: 'Küçük projeler için ideal',
    features: [
      '5 Monitor',
      '5 dakika kontrol aralığı',
      'Email bildirimleri',
      '7 gün veri saklama',
      'Temel metrikler',
      'Community destek',
    ],
    buttonText: 'Ücretsiz Başla',
    buttonType: 'free',
    popular: false,
  },
  {
    title: 'Professional',
    price: '₺199',
    period: '/ay',
    description: 'Büyüyen işletmeler için',
    features: [
      '50 Monitor',
      '1 dakika kontrol aralığı',
      'SMS + Email + Webhook',
      '30 gün veri saklama',
      'Gelişmiş metrikler',
      'SSL & Domain izleme',
      'API erişimi',
      'Öncelikli destek',
    ],
    buttonText: 'Başlayın',
    buttonType: 'solo',
    popular: true,
  },
  {
    title: 'Enterprise',
    price: 'Özel',
    period: 'Fiyat',
    description: 'Kurumsal çözümler',
    features: [
      'Sınırsız Monitor',
      '30 saniye kontrol aralığı',
      'Tüm bildirim kanalları',
      '365 gün veri saklama',
      'Özel metrikler',
      'Dedicated SLA',
      'White-label çözümler',
      '7/24 Premium destek',
    ],
    buttonText: 'İletişime Geçin',
    buttonType: 'private',
    popular: false,
  },
]

const testimonials = [
  {
    name: 'Ahmet Yılmaz',
    role: 'CTO, TechVenture',
    content: 'Rahat Up ile downtime süremizi %95 azalttık. Müşteri memnuniyeti rekor seviyeye ulaştı. Kesinlikle tavsiye ediyorum!',
    avatar: 'A',
    rating: 5,
    company: 'TechVenture',
  },
  {
    name: 'Elif Kara',
    role: 'DevOps Lead',
    content: 'API entegrasyonu sayesinde CI/CD pipeline\'ımıza kolayca entegre ettik. Artık her deployment öncesi otomatik sağlık kontrolü yapıyoruz.',
    avatar: 'E',
    rating: 5,
    company: 'CloudNative Co.',
  },
  {
    name: 'Mehmet Demir',
    role: 'E-commerce Manager',
    content: 'Black Friday\'de sistemimiz sorunsuz çalıştı. Rahat Up sayesinde olası sorunları önceden tespit edip önlem aldık.',
    avatar: 'M',
    rating: 5,
    company: 'ShopTR',
  },
]

const stats = [
  { value: '99.99%', label: 'Uptime Garantisi', icon: <TrendingUp /> },
  { value: '500+', label: 'Mutlu Müşteri', icon: <Star /> },
  { value: '10M+', label: 'Günlük Kontrol', icon: <Activity /> },
  { value: '<100ms', label: 'Yanıt Süresi', icon: <Zap /> },
]

const LandingPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const navigate = useNavigate()
  const { scrollYProgress } = useScroll()
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0])

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setMobileMenuOpen(false)
    }
  }

  const handlePricingButton = (type) => {
    switch(type) {
      case 'free':
      case 'solo':
        navigate('/register')
        break
      case 'private':
        scrollToSection('contact')
        break
      default:
        break
    }
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      message: ''
    },
    validationSchema: landingFormSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await api.post('users/landing', values)
        Swal.fire({
          icon: 'success',
          title: 'Mesajınız Gönderildi!',
          text: 'En kısa sürede sizinle iletişime geçeceğiz.',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        })
        resetForm()
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Hata!',
          text: error.response?.data?.message || 'Bir hata oluştu',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        })
      }
    },
  })

  return (
    <Box sx={{ overflow: 'hidden', bgcolor: 'background.default' }}>
      {/* Navbar */}
      <Box
        component={motion.nav}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: scrolled 
            ? alpha(theme.palette.background.paper, 0.95) 
            : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          transition: 'all 0.3s ease',
          borderBottom: scrolled 
            ? `1px solid ${alpha(theme.palette.primary.main, 0.1)}` 
            : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            py: 2 
          }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src="/rahatsistem-logo.png"
                alt="Rahat Up"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Rahat Up
              </Typography>
            </Stack>

            {!isMobile && (
              <Stack direction="row" spacing={1} alignItems="center">
                {['features', 'pricing', 'testimonials', 'contact'].map((item) => (
                  <Button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    sx={{
                      color: 'text.primary',
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      px: 2,
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    {item}
                  </Button>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{
                    ml: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  Giriş
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                  }}
                >
                  Başla
                </Button>
              </Stack>
            )}

            {isMobile && (
              <IconButton onClick={() => setMobileMenuOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          pt: 15,
          pb: 10,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.05)} 0%, ${alpha(theme.palette.background.default, 1)} 50%)`,
        }}
      >
        {/* Animated background elements */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              y: [0, -30, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
            sx={{
              position: 'absolute',
              width: { xs: 150, md: 250 },
              height: { xs: 150, md: 250 },
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.05
              )}, transparent)`,
              filter: 'blur(40px)',
              top: `${20 + i * 15}%`,
              left: `${10 + i * 20}%`,
              zIndex: 0,
            }}
          />
        ))}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Chip
                  icon={<Zap />}
                  label="YENİ NESİL MONITORING PLATFORMU"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    mb: 3,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />
                
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 900,
                    mb: 3,
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    lineHeight: 1.1,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${alpha(theme.palette.text.primary, 0.6)})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Sitenizi{' '}
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    7/24 İzleyin
                  </Box>
                  , Rahat Edin
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.7),
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  Web sitenizin performansını gerçek zamanlı takip edin, 
                  sorunları önceden tespit edin ve müşteri deneyimini optimize edin.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 6 }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/register')}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                      },
                    }}
                  >
                    Ücretsiz Deneyin
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PlayCircle />}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      borderWidth: 2,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    Demo İzle
                  </Button>
                </Stack>

                <Grid container spacing={3}>
                  {stats.map((stat, index) => (
                    <Grid item xs={6} sm={3} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              mb: 1,
                              color: 'primary.main',
                            }}
                          >
                            {stat.icon}
                          </Box>
                          <Typography variant="h4" fontWeight={900}>
                            {stat.value}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.background.paper,
                      0.9
                    )}, ${alpha(theme.palette.background.paper, 0.7)})`,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Box
                    component="img"
                    src={screenPng}
                    alt="Dashboard"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 10, md: 15 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Chip
              label="ÖZELLİKLER"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mb: 2,
                fontWeight: 700,
              }}
            />
            <Typography variant="h2" fontWeight={900} sx={{ mb: 2 }}>
              Güçlü Özellikler,{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Kusursuz Deneyim
              </Box>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              Modern ve kapsamlı monitoring araçlarıyla dijital varlıklarınızı koruyun
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.background.paper,
                        0.9
                      )}, ${alpha(theme.palette.background.paper, 0.6)})`,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 3,
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: 'white',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box 
        id="pricing" 
        sx={{ 
          py: { xs: 10, md: 15 },
          background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Chip
              label="FİYATLANDIRMA"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mb: 2,
                fontWeight: 700,
              }}
            />
            <Typography variant="h2" fontWeight={900} sx={{ mb: 2 }}>
              Her Bütçeye Uygun{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Planlar
              </Box>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              14 gün ücretsiz deneme. Kredi kartı gerektirmez.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      p: 4,
                      background: plan.popular
                        ? `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.1
                          )}, ${alpha(theme.palette.primary.main, 0.05)})`
                        : 'background.paper',
                      border: plan.popular
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: plan.popular ? 'scale(1.08)' : 'scale(1.03)',
                        boxShadow: `0 20px 60px ${alpha(
                          theme.palette.primary.main,
                          0.3
                        )}`,
                      },
                    }}
                  >
                    {plan.popular && (
                      <Chip
                        icon={<Star />}
                        label="EN POPÜLER"
                        sx={{
                          position: 'absolute',
                          top: -16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 800,
                          px: 2,
                        }}
                      />
                    )}

                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <Typography variant="h4" fontWeight={900} gutterBottom>
                        {plan.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {plan.description}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'center',
                          my: 3,
                        }}
                      >
                        <Typography variant="h2" fontWeight={900}>
                          {plan.price}
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {plan.period}
                        </Typography>
                      </Box>
                    </Box>

                    <Stack spacing={2} sx={{ flexGrow: 1, mb: 4 }}>
                      {plan.features.map((feature, idx) => (
                        <Stack key={idx} direction="row" spacing={2} alignItems="center">
                          <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Typography variant="body2">{feature}</Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      size="large"
                      fullWidth
                      onClick={() => handlePricingButton(plan.buttonType)}
                      sx={{
                        py: 1.5,
                        fontWeight: 700,
                        ...(plan.popular && {
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        }),
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                        },
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box id="testimonials" sx={{ py: { xs: 10, md: 15 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Chip
              label="MÜŞTERİLERİMİZ"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mb: 2,
                fontWeight: 700,
              }}
            />
            <Typography variant="h2" fontWeight={900} sx={{ mb: 2 }}>
              Başarı{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Hikayeleri
              </Box>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              Binlerce mutlu müşterimizin deneyimlerini keşfedin
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.background.paper,
                        0.9
                      )}, ${alpha(theme.palette.background.paper, 0.6)})`,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${alpha(
                          theme.palette.primary.main,
                          0.2
                        )}`,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: 'primary.main',
                          fontSize: '1.5rem',
                          fontWeight: 900,
                        }}
                      >
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={800}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                          }}
                        >
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.8, fontStyle: 'italic' }}
                    >
                      "{testimonial.content}"
                    </Typography>

                    <Stack direction="row" spacing={0.5}>
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          sx={{
                            color:
                              i < testimonial.rating
                                ? '#fbbf24'
                                : alpha('#fbbf24', 0.3),
                            fontSize: 24,
                          }}
                        />
                      ))}
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box 
        id="contact" 
        sx={{ 
          py: { xs: 10, md: 15 },
          background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Chip
              label="İLETİŞİM"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                mb: 2,
                fontWeight: 700,
              }}
            />
            <Typography variant="h2" fontWeight={900} sx={{ mb: 2 }}>
              Hemen{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Başlayın
              </Box>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              Sorularınız için 7/24 destek ekibimiz yanınızda
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  p: 4,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.background.paper,
                    0.9
                  )}, ${alpha(theme.palette.background.paper, 0.6)})`,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="name"
                        label="Adınız Soyadınız"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.default, 0.5),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="email"
                        label="E-posta"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.default, 0.5),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="message"
                        label="Mesajınız"
                        multiline
                        rows={5}
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.message && Boolean(formik.errors.message)}
                        helperText={formik.touched.message && formik.errors.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.default, 0.5),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{
                          py: 2,
                          fontWeight: 700,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha(
                              theme.palette.primary.main,
                              0.4
                            )}`,
                          },
                        }}
                      >
                        Mesaj Gönder
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  p: 4,
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.background.paper,
                    0.9
                  )}, ${alpha(theme.palette.background.paper, 0.6)})`,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  İletişim Bilgileri
                </Typography>
                
                <Stack spacing={3} sx={{ mt: 4 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <Email sx={{ color: 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        E-posta
                      </Typography>
                      <Typography fontWeight={600}>
                        covhandata@gmail.com
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <Phone sx={{ color: 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Telefon
                      </Typography>
                      <Typography fontWeight={600}>
                        +90 (506) 315 88 12
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <LocationOn sx={{ color: 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Adres
                      </Typography>
                      <Typography fontWeight={600}>
                        Çifte Havuzlar Mah. Eski Londra Asfaltı Cad.
                        <br />
                        Kuluçka Merkezi A1 Blok No: 151/1C
                        <br />
                        İç Kapı No: B35 Esenler/İSTANBUL
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                <Box sx={{ mt: 6 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Sosyal Medya
                  </Typography>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                    {[Facebook, Twitter, LinkedIn, Instagram].map(
                      (Icon, index) => (
                        <IconButton
                          key={index}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              transform: 'translateY(-4px)',
                            },
                          }}
                        >
                          <Icon />
                        </IconButton>
                      )
                    )}
                  </Stack>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 6,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  component="img"
                  src="/rahatsistem-logo.png"
                  alt="Logo"
                  sx={{ width: 40, height: 40, borderRadius: 2 }}
                />
                <Typography variant="h6" fontWeight={900}>
                  Rahat Up
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Web sitenizi güvenle izleyin, rahat edin. Modern monitoring
                çözümleri ile dijital varlıklarınızı koruyun.
              </Typography>
            </Grid>

            <Grid item xs={12} md={2}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Ürün
              </Typography>
              <Stack spacing={1}>
                {['Özellikler', 'Fiyatlandırma', 'API', 'Durum'].map((item) => (
                  <Button
                    key={item}
                    sx={{
                      justifyContent: 'flex-start',
                      color: 'text.secondary',
                      textTransform: 'none',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Şirket
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Covhan Data Arge Yazılım
                <br />
                Danışmanlık Limited Şirketi
                <br />
                Vergi No: 1234567890
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Bülten
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Güncellemelerden haberdar olun
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="E-posta"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.default, 0.5),
                    },
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  }}
                >
                  Abone
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 6,
              pt: 4,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Rahat Up. Tüm hakları saklıdır.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Mobile Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.95
            )}, ${alpha(theme.palette.background.paper, 0.9)})`,
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Typography variant="h6" fontWeight={900}>
              Rahat Up
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            {['features', 'pricing', 'testimonials', 'contact'].map((item) => (
              <ListItem
                key={item}
                button
                onClick={() => scrollToSection(item)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <ListItemText
                  primary={item.charAt(0).toUpperCase() + item.slice(1)}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>

          <Stack spacing={2} sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/login')}
            >
              Giriş Yap
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/register')}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              }}
            >
              Ücretsiz Başla
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  )
}

export default LandingPage